/**
 * Lista de canais do servidor aberto.
 *
 * Canais de voz mostram os participantes logo abaixo do nome, expandidos. Isso
 * é o que faz alguém entrar numa conversa: você vê quem já está lá antes de
 * clicar, em vez de precisar entrar para descobrir que a sala está vazia.
 *
 * Quase tudo o que se faz com a estrutura do servidor está no botão direito, e
 * não escondido dentro das configurações. Reorganizar canais é uma tarefa feita
 * olhando a própria lista; obrigar a abrir um painel separado para renomear uma
 * categoria tira a pessoa justamente do lugar onde ela está vendo o problema.
 */

import { useState } from 'react'
import {
  ChevronDown,
  FolderPlus,
  Hash,
  Lock,
  MonitorUp,
  Pencil,
  Plus,
  Settings,
  Trash2,
  UserPlus,
  Video,
  Volume2,
} from 'lucide-react'
import {
  LIMITS,
  Permission,
  hasPermission,
  type Category,
  type Channel,
  type GuildMember,
  type VoiceState,
} from '@nexus/shared'
import { cn } from '@/lib/cn'
import { api } from '@/lib/api'
import { UserContextMenu, type PosicaoDoMenu } from '@/components/layout/UserContextMenu'
import { NicknamePrompt } from '@/components/layout/NicknamePrompt'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useVoiceStore } from '@/store/voice'
import {
  useChannelVoiceStates,
  useGuildChannelTree,
  useGuildPermissions,
  useMember,
  useUnreadCount,
} from '@/hooks/selectors'
import { Avatar, ContextMenu, MenuItem, MenuSeparator, Tooltip } from '@/components/ui'
import { ConfirmDialog, NamePrompt } from '@/components/ui/dialogs'
import { CreateChannelModal } from '@/components/layout/CreateChannelModal'
import { EditChannelModal } from '@/components/layout/EditChannelModal'
import { GuildSettingsModal } from '@/components/layout/GuildSettingsModal'
import { UserPanel } from '@/components/layout/UserPanel'
import { VoiceStatusPanel } from '@/components/voice/VoiceStatusPanel'

/** Alvo de um menu de contexto aberto, com o ponto onde o clique aconteceu. */
type MenuDeCanal = PosicaoDoMenu & { canal: Channel }
type MenuDeCategoria = PosicaoDoMenu & { categoria: Category }

export function ChannelSidebar() {
  const currentGuildId = useAppStore((s) => s.currentGuildId)
  const guild = useAppStore((s) => (s.currentGuildId ? s.guilds[s.currentGuildId] : null))
  const currentChannelId = useAppStore((s) => s.currentChannelId)
  const voiceChannelId = useVoiceStore((s) => s.channelId)
  const applyEvent = useAppStore((s) => s.applyEvent)

  const permissoes = useGuildPermissions(currentGuildId)
  const podeGerenciarCanais = hasPermission(permissoes, Permission.MANAGE_CHANNELS)
  const podeConvidar = hasPermission(permissoes, Permission.CREATE_INVITE)

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [creatingIn, setCreatingIn] = useState<{ categoryId: string | null } | null>(null)
  const [settingsOpen, setSettingsOpen] = useState<'invites' | 'general' | null>(null)
  const [menu, setMenu] = useState<PosicaoDoMenu | null>(null)
  const [menuCanal, setMenuCanal] = useState<MenuDeCanal | null>(null)
  const [menuCategoria, setMenuCategoria] = useState<MenuDeCategoria | null>(null)

  const [criandoCategoria, setCriandoCategoria] = useState(false)
  const [editandoCanal, setEditandoCanal] = useState<Channel | null>(null)
  const [renomeandoCategoria, setRenomeandoCategoria] = useState<Category | null>(null)
  const [excluindoCanal, setExcluindoCanal] = useState<Channel | null>(null)
  const [excluindoCategoria, setExcluindoCategoria] = useState<Category | null>(null)

  // Deriva da árvore reativa: o hook assina os dicionários de canais e
  // categorias, então criar ou remover canal atualiza a lista na hora.
  const tree = useGuildChannelTree(currentGuildId)

  if (!guild) {
    return <aside aria-label="Canais" className="w-sidebar shrink-0 bg-surface-sunken" />
  }

  const toggleCategory = (categoryId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) next.delete(categoryId)
      else next.add(categoryId)
      return next
    })
  }

  return (
    <aside aria-label="Canais" className="flex w-sidebar shrink-0 flex-col bg-surface-sunken">
      <button
        type="button"
        onClick={(e) => setMenu({ x: e.clientX, y: e.clientY })}
        onContextMenu={(e) => {
          e.preventDefault()
          setMenu({ x: e.clientX, y: e.clientY })
        }}
        className="flex h-12 shrink-0 items-center justify-between gap-2 px-4 text-left transition-colors hover:bg-surface-hover"
      >
        <span className="truncate text-[15px] font-semibold text-content-primary">{guild.name}</span>
        <ChevronDown className="size-4 shrink-0 text-content-secondary" />
      </button>

      <div
        className="scroll-region flex-1 px-2 pt-2 pb-3"
        onContextMenu={(e) => {
          e.preventDefault()
          setMenu({ x: e.clientX, y: e.clientY })
        }}
      >
        {tree.uncategorized.length > 0 ? (
          <div className="mb-2">
            {tree.uncategorized.map((channel) => (
              <ChannelRow
                key={channel.id}
                channel={channel}
                active={channel.id === currentChannelId}
                onMenu={(posicao) => setMenuCanal({ ...posicao, canal: channel })}
              />
            ))}
          </div>
        ) : null}

        {tree.categories.map((category) => {
          const channels = tree.byCategory[category.id] ?? []
          const isCollapsed = collapsed.has(category.id)

          // Um canal aberto ou com gente em chamada continua visível mesmo com a
          // categoria recolhida: esconder o que está em uso confunde.
          const visible = isCollapsed
            ? channels.filter((c) => c.id === currentChannelId || c.id === voiceChannelId)
            : channels

          return (
            <section key={category.id} className="mb-3">
              <div
                className="group flex items-center gap-0.5 px-1"
                onContextMenu={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setMenuCategoria({ x: e.clientX, y: e.clientY, categoria: category })
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  className="flex flex-1 items-center gap-0.5 py-1 text-left"
                >
                  <ChevronDown
                    className={cn(
                      'size-3 shrink-0 text-content-muted transition-transform duration-150',
                      isCollapsed && '-rotate-90',
                    )}
                  />
                  <span className="truncate text-[11px] font-bold tracking-wider text-content-muted uppercase transition-colors group-hover:text-content-secondary">
                    {category.name}
                  </span>
                </button>

                {podeGerenciarCanais ? (
                  <Tooltip content="Criar canal" side="top">
                    <button
                      type="button"
                      onClick={() => setCreatingIn({ categoryId: category.id })}
                      aria-label={`Criar canal em ${category.name}`}
                      className="revelar-no-hover rounded p-0.5 text-content-muted hover:text-content-primary"
                    >
                      <Plus className="size-4" />
                    </button>
                  </Tooltip>
                ) : null}
              </div>

              {visible.map((channel) => (
                <ChannelRow
                  key={channel.id}
                  channel={channel}
                  active={channel.id === currentChannelId}
                  onMenu={(posicao) => setMenuCanal({ ...posicao, canal: channel })}
                />
              ))}
            </section>
          )
        })}
      </div>

      <VoiceStatusPanel />
      <UserPanel />

      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          {podeGerenciarCanais ? (
            <>
              <MenuItem icon={<Plus />} onClick={() => setCreatingIn({ categoryId: null })}>
                Criar canal
              </MenuItem>
              <MenuItem icon={<FolderPlus />} onClick={() => setCriandoCategoria(true)}>
                Criar categoria
              </MenuItem>
            </>
          ) : null}

          {podeConvidar ? (
            <>
              {podeGerenciarCanais ? <MenuSeparator /> : null}
              <MenuItem icon={<UserPlus />} onClick={() => setSettingsOpen('invites')}>
                Convidar pessoas
              </MenuItem>
            </>
          ) : null}

          <MenuSeparator />
          <MenuItem icon={<Settings />} onClick={() => setSettingsOpen('general')}>
            Configurações do servidor
          </MenuItem>
        </ContextMenu>
      ) : null}

      {menuCanal ? (
        <ContextMenu x={menuCanal.x} y={menuCanal.y} onClose={() => setMenuCanal(null)}>
          <div className="px-2.5 py-1.5">
            <p className="truncate text-[13px] font-semibold text-content-primary">
              {menuCanal.canal.name}
            </p>
          </div>
          <MenuSeparator />

          {podeGerenciarCanais ? (
            <>
              <MenuItem icon={<Pencil />} onClick={() => setEditandoCanal(menuCanal.canal)}>
                Editar canal
              </MenuItem>
              <MenuItem
                icon={<Plus />}
                onClick={() => setCreatingIn({ categoryId: menuCanal.canal.categoryId })}
              >
                Criar canal ao lado
              </MenuItem>
              <MenuSeparator />
              <MenuItem icon={<Trash2 />} danger onClick={() => setExcluindoCanal(menuCanal.canal)}>
                Excluir canal
              </MenuItem>
            </>
          ) : (
            <MenuItem icon={<Hash />} disabled>
              Sem permissão para editar
            </MenuItem>
          )}
        </ContextMenu>
      ) : null}

      {menuCategoria ? (
        <ContextMenu x={menuCategoria.x} y={menuCategoria.y} onClose={() => setMenuCategoria(null)}>
          <div className="px-2.5 py-1.5">
            <p className="truncate text-[13px] font-semibold text-content-primary">
              {menuCategoria.categoria.name}
            </p>
          </div>
          <MenuSeparator />

          {podeGerenciarCanais ? (
            <>
              <MenuItem
                icon={<Plus />}
                onClick={() => setCreatingIn({ categoryId: menuCategoria.categoria.id })}
              >
                Criar canal aqui
              </MenuItem>
              <MenuItem
                icon={<Pencil />}
                onClick={() => setRenomeandoCategoria(menuCategoria.categoria)}
              >
                Renomear categoria
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                icon={<Trash2 />}
                danger
                onClick={() => setExcluindoCategoria(menuCategoria.categoria)}
              >
                Excluir categoria
              </MenuItem>
            </>
          ) : (
            <MenuItem icon={<Hash />} disabled>
              Sem permissão para editar
            </MenuItem>
          )}
        </ContextMenu>
      ) : null}

      <CreateChannelModal
        open={creatingIn !== null}
        guildId={guild.id}
        categoryId={creatingIn?.categoryId ?? null}
        onClose={() => setCreatingIn(null)}
      />

      <NamePrompt
        open={criandoCategoria}
        title="Criar categoria"
        label="Nome da categoria"
        placeholder="Canais de voz"
        confirmLabel="Criar categoria"
        maxLength={LIMITS.categoryName.max}
        onConfirm={async (nome) => {
          const categoria = await api.guilds.createCategory(guild.id, nome)
          applyEvent({ op: 'category_create', d: categoria, s: 0 })
        }}
        onClose={() => setCriandoCategoria(false)}
      />

      <EditChannelModal channel={editandoCanal} onClose={() => setEditandoCanal(null)} />

      <NamePrompt
        open={renomeandoCategoria !== null}
        title="Renomear categoria"
        label="Nome da categoria"
        initialValue={renomeandoCategoria?.name ?? ''}
        maxLength={LIMITS.categoryName.max}
        onConfirm={async (nome) => {
          if (!renomeandoCategoria) return
          const categoria = await api.guilds.updateCategory(guild.id, renomeandoCategoria.id, nome)
          applyEvent({ op: 'category_update', d: categoria, s: 0 })
        }}
        onClose={() => setRenomeandoCategoria(null)}
      />

      <ConfirmDialog
        open={excluindoCanal !== null}
        title="Excluir canal"
        description={`O canal "${excluindoCanal?.name ?? ''}" e todas as mensagens dele somem para todo mundo, sem como desfazer.`}
        confirmLabel="Excluir canal"
        onConfirm={async () => {
          if (!excluindoCanal) return
          await api.channels.remove(excluindoCanal.id)
          applyEvent({
            op: 'channel_delete',
            d: { id: excluindoCanal.id, guildId: excluindoCanal.guildId },
            s: 0,
          })
        }}
        onClose={() => setExcluindoCanal(null)}
      />

      <ConfirmDialog
        open={excluindoCategoria !== null}
        title="Excluir categoria"
        description={`A categoria "${excluindoCategoria?.name ?? ''}" some da lista. Os canais que estavam dentro dela continuam existindo, soltos no topo da barra lateral.`}
        confirmLabel="Excluir categoria"
        onConfirm={async () => {
          if (!excluindoCategoria) return
          await api.guilds.deleteCategory(guild.id, excluindoCategoria.id)
          applyEvent({
            op: 'category_delete',
            d: { id: excluindoCategoria.id, guildId: guild.id },
            s: 0,
          })
        }}
        onClose={() => setExcluindoCategoria(null)}
      />

      <GuildSettingsModal
        open={settingsOpen !== null}
        guildId={guild.id}
        abaInicial={settingsOpen ?? 'invites'}
        onClose={() => setSettingsOpen(null)}
      />
    </aside>
  )
}

function ChannelRow({
  channel,
  active,
  onMenu,
}: {
  channel: Channel
  active: boolean
  onMenu: (posicao: PosicaoDoMenu) => void
}) {
  const selectChannel = useAppStore((s) => s.selectChannel)
  const voiceChannelId = useVoiceStore((s) => s.channelId)
  const connectVoice = useVoiceStore((s) => s.connect)
  const connecting = useVoiceStore((s) => s.connecting)

  const participants = useChannelVoiceStates(channel.type === 'voice' ? channel.id : null)
  const unreadCount = useUnreadCount(channel.id)
  const unread = channel.type === 'text' ? unreadCount : 0
  const isVoiceActive = voiceChannelId === channel.id

  const handleClick = () => {
    if (channel.type === 'voice') {
      if (!isVoiceActive && !connecting) {
        void connectVoice(channel.id, channel.guildId)
      }
      selectChannel(channel.id)
    } else {
      selectChannel(channel.id)
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onMenu({ x: e.clientX, y: e.clientY })
        }}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'group flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left transition-colors',
          active
            ? 'bg-surface-active text-content-primary'
            : unread > 0
              ? 'text-content-primary hover:bg-surface-hover'
              : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
        )}
      >
        {channel.type === 'text' ? (
          <Hash className="size-[18px] shrink-0 text-content-muted" />
        ) : (
          <Volume2 className="size-[18px] shrink-0 text-content-muted" />
        )}

        <span className={cn('flex-1 truncate text-[15px]', unread > 0 && 'font-semibold')}>
          {channel.name}
        </span>

        {channel.userLimit > 0 && channel.type === 'voice' ? (
          <span className="shrink-0 text-[11px] text-content-muted">
            {participants.length}/{channel.userLimit}
          </span>
        ) : null}

        {unread > 0 ? (
          <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {participants.length > 0 ? (
        <div className="mt-0.5 mb-1 ml-6 flex flex-col gap-0.5">
          {participants.map((state) => (
            <VoiceParticipantRow key={state.userId} state={state} guildId={channel.guildId} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function VoiceParticipantRow({ state, guildId }: { state: VoiceState; guildId: string }) {
  const member = useMember(guildId, state.userId)
  const myId = useAuthStore((s) => s.user?.id)
  const remotes = useVoiceStore((s) => s.remotes)
  const localSpeaking = useVoiceStore((s) => s.localSpeaking)

  const [menu, setMenu] = useState<PosicaoDoMenu | null>(null)
  const [apelidoDe, setApelidoDe] = useState<GuildMember | null>(null)

  const isMe = state.userId === myId
  const speaking = isMe ? localSpeaking : (remotes[state.userId]?.speaking ?? false)
  const name = member?.nickname ?? member?.user.displayName ?? 'Usuário'

  return (
    <div
      onContextMenu={(event) => {
        event.preventDefault()
        event.stopPropagation()
        setMenu({ x: event.clientX, y: event.clientY })
      }}
      className="flex items-center gap-2 rounded px-1.5 py-1 transition-colors hover:bg-surface-hover"
    >
      {menu ? (
        <UserContextMenu
          userId={state.userId}
          guildId={guildId}
          posicao={menu}
          nomeDeReserva={name}
          onAlterarApelido={setApelidoDe}
          onClose={() => setMenu(null)}
        />
      ) : null}

      <NicknamePrompt membro={apelidoDe} guildId={guildId} onClose={() => setApelidoDe(null)} />


      <Avatar
        userId={state.userId}
        name={name}
        src={member?.user.avatarUrl}
        size="xs"
        speaking={speaking}
      />
      <span
        className={cn(
          'flex-1 truncate text-[13px]',
          state.selfMute || state.serverMute ? 'text-content-muted' : 'text-content-secondary',
        )}
      >
        {name}
      </span>

      <div className="flex shrink-0 items-center gap-1 text-content-muted">
        {state.screenSharing ? (
          <Tooltip content="Compartilhando a tela">
            <MonitorUp className="size-3.5 text-success" />
          </Tooltip>
        ) : null}
        {state.cameraOn ? <Video className="size-3.5" /> : null}
        {state.serverMute ? (
          <Tooltip content="Silenciado pela moderação">
            <Lock className="size-3.5 text-danger" />
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}
